<template>
  <div class="modelReg">
    <div class="modelReg__grid">
      <div class="modelReg__title">
        <h3>Создать новый товар</h3>
      </div>

      <div v-if="loader" class="modelReg__alertLoading"></div>
      <div v-if="errorMessage" class="modelReg__alertError">{{ errorMessage }}</div>
      <div v-if="successMessage" class="modelReg__alertSuccess">{{ successMessage }}</div>

      <form class="modelReg__form" autocomplete="off">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <UiBaseInputN
            v-model="form.name"
            label="Название"
            type-input="text"
            class="w-full"
            :error="v$.name.$error ? 'Ошибка валидации' : ''"
            :errors="v$.name.$errors"
            :show-error="true"
            @change="v$.name.$touch"
          />

          <UiBaseInputN
            v-model.number="form.price"
            label="Цена"
            type-input="number"
            class="w-full"
            :error="v$.price.$error ? 'Ошибка валидации' : ''"
            :errors="v$.price.$errors"
            :show-error="true"
            @change="v$.price.$touch"
          />

          <UiBaseInputN
            v-model="form.description"
            label="Описание"
            type-input="textarea"
            class="w-full sm:col-span-2"
          />

          <UiBaseInputN
            v-model.number="form.amount"
            label="Количество"
            type-input="number"
            currency="шт."
            class="w-full"
          />

          <UiBaseInputN
            v-model.number="form.volume"
            label="Объем"
            type-input="number"
            currency="мл."
            class="w-full"
          />

          <UiBaseInputN
            v-model.number="form.weight"
            label="Вес"
            type-input="number"
            currency="г."
            class="w-full"
          />

          <UiBaseInputN
            v-model="form.size"
            label="Размер"
            type-input="text"
            class="w-full"
          />

          <UiBaseInputN
            v-model="form.product_id"
            label="Артикул"
            type-input="text"
            class="w-full"
            :error="v$.product_id.$error ? 'Ошибка валидации' : ''"
            :errors="v$.product_id.$errors"
            :show-error="true"
            @change="v$.product_id.$touch"
          />

          <UiBaseInputN
            v-model.number="form.stock"
            label="На складе"
            type-input="number"
            class="w-full"
          />

          <UiBaseInputN
            v-model.number="form.reserved"
            label="В резерве"
            type-input="number"
            class="w-full"
          />

          <UiBaseInputN
            v-model.number="form.sort_order"
            label="Сортировка"
            type-input="number"
            class="w-full"
          />

          <!-- Категория -->
          <USelectMenu
            v-model="form.category_id"
            :options="categoryOptions"
            value-attribute="id"
            option-attribute="name"
            placeholder="Категория"
            size="xl"
            class="w-full"
            :clearable="true"
          />

          <!-- Группа товаров -->
          <USelectMenu
            v-model="form.group_id"
            :options="groupOptions"
            value-attribute="id"
            option-attribute="name"
            placeholder="Группа товаров"
            size="xl"
            class="w-full"
            :clearable="true"
          />

          <!-- Филиалы -->
          <USelectMenu
            v-model="form.branches"
            :options="branchOptions"
            multiple
            value-attribute="id"
            option-attribute="label"
            size="xl"
            class="w-full sm:col-span-2"
          >
            <template #label>
              <span v-if="form.branches.length" class="truncate">
                {{
                  branchOptions
                    .filter((opt) => form.branches.includes(opt.id))
                    .map((opt) => opt.label)
                    .join(', ')
                }}
              </span>
              <span v-else class="text-gray-500">Выберите филиалы</span>
            </template>
          </USelectMenu>

          <!-- Добавки -->
          <USelectMenu
            v-model="form.addons"
            :options="addons"
            multiple
            value-attribute="id"
            option-attribute="name"
            size="xl"
            class="w-full sm:col-span-2"
            :searchable="true"
            :clearable="true"
          >
            <template #label>
              <span v-if="Array.isArray(form.addons) && form.addons.length" class="truncate">
                {{
                  addons
                    .filter((a) => form.addons.includes(a.id))
                    .map((a) => a.name)
                    .join(', ')
                }}
              </span>
              <span v-else class="text-gray-500">Выберите добавки</span>
            </template>
          </USelectMenu>

          <!-- Отбавки -->
          <USelectMenu
            v-model="form.exclude_ingredients"
            :options="excludeIngredients"
            multiple
            value-attribute="id"
            option-attribute="name"
            size="xl"
            class="w-full sm:col-span-2"
            :searchable="true"
            :clearable="true"
          >
            <template #label>
              <span v-if="Array.isArray(form.exclude_ingredients) && form.exclude_ingredients.length" class="truncate">
                {{
                  excludeIngredients
                    .filter((a) => form.exclude_ingredients.includes(a.id))
                    .map((a) => a.name)
                    .join(', ')
                }}
              </span>
              <span v-else class="text-gray-500">Выберите отбавки</span>
            </template>
          </USelectMenu>

          <!-- KBJU поля -->
          <UiBaseInputN
            v-model="form.fats"
            label="Жиры"
            type-input="number"
            class="w-full"
          />
          <UiBaseInputN
            v-model="form.proteins"
            label="Белки"
            type-input="number"
            class="w-full"
          />
          <UiBaseInputN
            v-model="form.carbohydrates"
            label="Углеводы"
            type-input="number"
            class="w-full"
          />
          <UiBaseInputN
            v-model="form.calories"
            label="Калории"
            type-input="number"
            class="w-full"
          />

          <!-- Статусы -->
          <div class="flex items-center space-x-4 sm:col-span-2">
            <div class="flex items-center space-x-2">
              <label class="font-medium">Активен</label>
              <input v-model="form.is_active" type="checkbox" class="ml-2" />
            </div>
            <div class="flex items-center space-x-2">
              <label class="font-medium">В стоп-листе</label>
              <input v-model="form.stop_list" type="checkbox" class="ml-2" />
            </div>
          </div>

          <!-- Изображение -->
          <div class="relative flex flex-col items-center sm:col-span-2">
            <div
              class="w-24 h-24 md:w-36 md:h-36 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center border border-gray-300 mb-2"
            >
              <img v-if="form.previewUrl" :src="form.previewUrl" class="object-cover w-full h-full" />
              <span v-else class="text-gray-500 text-center text-xs md:text-sm">Загрузить изображение</span>
            </div>
            <input
              type="file"
              accept="image/*"
              class="text-sm text-gray-600 absolute top-0 right-0 w-full h-full opacity-0 z-30 cursor-pointer"
              @change="(event) => onImageChange(event)"
            />
          </div>
        </div>

        <div class="modelReg__grid-btn">
          <UiButtonMain
            label="Отмена"
            class="bg-gray-500 hover:bg-gray-600 text-white"
            @click="closeModal"
          />
          <UiButtonMain
            label="Создать товар"
            class="bg-blue-600 hover:bg-blue-700 text-white"
            :disabled="v$.$invalid || loader"
            @click="submitForm"
          />
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useVuelidate } from '@vuelidate/core';
import { required, minValue } from '@vuelidate/validators';
import { consola } from 'consola';
import { apiAdminProduct , APIAdminInstitution , apiAdminCategory , APIAdminAddons , APIAdminExcludeIngredients } from '~/fsd-pages/admin/api';

import type { Branch } from '@entities/institution';
import type { Category , ProductGroup } from '@entities/product';




import { getReadyInstitutionId, getBranchLabel } from '@entities/institution';

const emit = defineEmits<{
  'close': []
  'product-created': []
  'update:size': [size: string]
}>();

const logger = consola.withTag('AddProductModal');

const loader = ref(false);
const errorMessage = ref<string | null>(null);
const successMessage = ref<string | null>(null);

// Данные для формы
const addons = ref<any[]>([]);
const excludeIngredients = ref<any[]>([]);
const categories = ref<Category[]>([]);
const productGroups = ref<ProductGroup[]>([]);
const branchess = ref<Branch[]>([]);

// Форма
const form = ref({
  name: '',
  description: '',
  price: '',
  amount: '',
  volume: '',
  weight: '',
  size: '',
  product_id: '',
  stock: '',
  reserved: '',
  sort_order: '',
  category_id: undefined,
  group_id: undefined,
  branches: [] as number[],
  addons: [] as number[],
  exclude_ingredients: [] as number[],
  is_active: true,
  stop_list: false,
  previewUrl: undefined as string | undefined,
  _file: undefined as File | undefined,
  fats: '',
  calories: '',
  proteins: '',
  carbohydrates: '',
});

// Валидация
const rules = {
  name: { required },
  price: { required, minValue: minValue(0) },
  product_id: { required },
};

const v$ = useVuelidate(rules, form);

// Опции для селектов
const categoryOptions = computed(() => [
  { id: null, name: '— Не выбрано —' },
  ...categories.value,
]);

const groupOptions = computed(() => [
  { id: null, name: '— Не выбрано —' },
  ...productGroups.value,
]);

const branchOptions = computed(() =>
  branchess.value.map((b) => ({
    id: Number(b.id),
    label: getBranchLabel(b),
  })),
);

// Загрузка данных
const loadData = async () => {
  try {
    // Группы товаров
    const groupsRaw = await apiAdminProduct.getProductGroups();
    productGroups.value = groupsRaw.groups.map((g) => ({
      ...g,
      id: prepareId(g.id) || 0,
      name: g.name,
    }));

    // Категории
    const categoriesRaw = await apiAdminCategory.getAll();
    categories.value = categoriesRaw.map((c) => ({
      ...c,
      id: prepareId(c.id) || 0,
      previewUrl: undefined,
    }));

    // Добавки
    const ads = await APIAdminAddons.getAll();
    addons.value = ads.map((a) => ({
      ...a,
      id: Number(a.id),
      previewUrl: undefined,
      _file: undefined,
    }));

    // Отбавки
    const exs = await APIAdminExcludeIngredients.getAll();
    excludeIngredients.value = exs.map((e) => ({
      ...e,
      id: Number(e.id),
      previewUrl: undefined,
      _file: undefined,
    }));

    // Филиалы
    const institutionID = await getReadyInstitutionId();
    const inst = await APIAdminInstitution.getInstitutionWithBranches(institutionID);
    branchess.value = inst.branches;

  } catch (error: any) {
    logger.error('Ошибка загрузки данных:', error);
    errorMessage.value = 'Ошибка загрузки данных';
  }
};

// Вспомогательные функции
const prepareId = (val: any) => {
  if (val === undefined || val === null || val === '' || Number(val) === 0) return null;
  const n = Number(val);
  return Number.isNaN(n) ? null : n;
};

const collectKbju = (prod: any) => {
  if (![prod.fats, prod.calories, prod.proteins, prod.carbohydrates].some((v) => v !== '' && v != null)) return null;
  return {
    fats: prod.fats ?? '',
    calories: prod.calories ?? '',
    proteins: prod.proteins ?? '',
    carbohydrates: prod.carbohydrates ?? '',
  };
};

const onImageChange = async (event: Event) => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;

  form.value.previewUrl = URL.createObjectURL(file);
  form.value._file = file;
};

const submitForm = async () => {
  // Временно отключаем валидацию для тестирования
  // const isValid = await v$.value.$validate();
  // if (!isValid) return;

  loader.value = true;
  errorMessage.value = null;
  successMessage.value = null;

  try {
    const fd = new FormData();
    Object.entries(form.value).forEach(([key, val]) => {
      if (key === '_file' && val) {
        fd.append('images', val as File);
      } else if (key === 'branches' || key === 'addons' || key === 'exclude_ingredients') {
        if (Array.isArray(val)) {
          fd.append(key, JSON.stringify(val.map(Number)));
        }
      } else if (
        [
          'img', 'editingImage', 'previewUrl', 'category', 'products_branches',
          'fats', 'calories', 'proteins', 'carbohydrates',
        ].includes(key)
      ) {
        // служебные поля — на бэкенд не отправляем
      } else if (key === 'category_id' || key === 'group_id') {
        const v = prepareId(val);
        if (v !== null) fd.append(key, String(v));
      } else if (val !== undefined && val !== null && val !== '') {
        fd.append(key, String(val));
      }
    });

    const kbjuObj = collectKbju(form.value);
    if (kbjuObj) fd.append('kbju', JSON.stringify(kbjuObj));

    await apiAdminProduct.create(fd as any);

    successMessage.value = 'Товар успешно создан!';
    emit('product-created');

    setTimeout(() => {
      closeModal();
    }, 1500);

  } catch (error: any) {
    logger.error('Ошибка создания товара:', error);
    errorMessage.value = error?.data?.message || 'Ошибка создания товара';
  } finally {
    loader.value = false;
  }
};

const closeModal = () => {
  emit('close');
};

// Инициализация
onMounted(() => {
  emit('update:size', 'l');
  loadData();
});
</script>

<style scoped lang="scss">
// Используем глобальные стили modelReg из authModal.scss
</style>